import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { PathName, getRouteByKey } from 'utility/routes';
import Persist, { StorageKind } from 'lib/persist';
import { RootState, useAppDispatch } from 'lib/centralStore';
import { SubmitHandler, useForm } from 'react-hook-form';

import { BonusCarousel } from 'components/bonusCarousel';
import { ButtonRegistrationWrapper } from '../buttonRegistrationWrapper';
import { DatoCmsImage } from 'lib/datoCms/types';
import { REGISTRATION_STEP_KEY } from 'features/signup/constants';
import { RichText } from 'components/richText';
import { TextInput } from 'components/inputs/textInput';
import { convertInImgType } from 'lib/datoCms/utils';
import { gtag } from 'utility/functions';
import { setPromoCode } from 'features/signup/signup';
import styles from 'components/page-content/registrati/registrati.module.scss';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const localStorage = Persist(StorageKind.local);

export type PromoBonusProps = {
  next: Dispatch<SetStateAction<boolean>>;
  back?: Dispatch<SetStateAction<boolean>>;
  labels: {
    titlePromoBonus: string;
    titlePromoBonusAffiliate: string;
    descriptionPromoBonus: string;
    descriptionPromoBonusAffiliate: string;
    descriptionPromoCode: string;
    promoCode: string;
    placeholderPromoCode: string;
    errorMessagePromoCode: string;
    bonusList: Array<RegistrationBonus>;
  };
};
export type RegistrationBonus = {
  id: string;
  typeOfBonus: string;
  text: string;
  image: DatoCmsImage;
  promoCode: string;
  informationTitle: string;
  informationText: string;
};

export const PromoBonus: React.FC<PromoBonusProps> = ({ next, back, labels }: PromoBonusProps) => {
  const state = useSelector((store: RootState) => store.signup);
  const promoCode = useSelector((store: RootState) => store.signup.promoCode);
  const [bonusSelected, setBonusSelected] = useState<string>(promoCode);
  const [promoHasMatched, setPromoHasMatched] = useState<boolean>(!!promoCode);
  const registrationUrl = useMemo(() => getRouteByKey(PathName.registrationUrl), []);

  const labelOppure = useGetLabelByKey('oppure')!;
  const { asPath, query } = useRouter();
  const dispatch = useAppDispatch();
  const bonusListRegistration = useMemo(
    () =>
      labels.bonusList.map((bonus) => {
        return {
          id: bonus.id,
          typeOfBonus: bonus.typeOfBonus,
          text: bonus.text,
          bgImage: convertInImgType(bonus.image),
          promoCode: bonus.promoCode,
          informationTitle: bonus.informationTitle,
          informationText: bonus.informationText,
        };
      }),
    [labels.bonusList]
  );
  const promoList = bonusListRegistration.map((item) => item?.promoCode);

  const bonusCodeSchema = z
    .object({ promocode: z.string() })
    .refine(
      (schema) =>
        !state.provenienceCode && !codPromoFromUrl ? promoList.includes(schema.promocode) : schema.promocode,
      {
        message: labels.errorMessagePromoCode,
        path: ['promocode'],
      }
    );
  type BonusCodeValidation = z.infer<typeof bonusCodeSchema>;
  const { control, formState, watch, trigger, setValue, getValues, handleSubmit } = useForm<BonusCodeValidation>({
    resolver: zodResolver(bonusCodeSchema),
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: { promocode: promoCode },
  });

  const codPromoFromUrl = useMemo(() => {
    return query['cod_promo'] || query['COD_PROMO'] || query['cod_prom'] || query['COD_PROM'];
  }, [query]);

  const isBlockVisible = useMemo(() => {
    if (codPromoFromUrl) return promoList.some((promo) => promo === codPromoFromUrl);

    return true;
  }, [codPromoFromUrl, promoList]);

  const handlePromoSelected = useCallback(
    (promoCode: string) => {
      setValue('promocode', promoCode);
      trigger('promocode');
    },
    [setValue, trigger]
  );

  useEffect(() => {
    if (promoCode) {
      handlePromoSelected(promoCode);
      checkPromoCode(promoCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCode]);

  const checkPromoCode = useCallback(
    (promoCode: string) => {
      const isPromoCodePresentInList = promoList.some(
        (promo: string) => promo.toUpperCase() === promoCode.toUpperCase()
      );
      setPromoHasMatched(isPromoCodePresentInList);
      setBonusSelected(promoCode);
    },
    [promoList]
  );

  useEffect(() => {
    checkPromoCode(watch()?.promocode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch()?.promocode]);

  useEffect(() => {
    if (asPath.includes(registrationUrl)) {
      const step = localStorage.getItem(REGISTRATION_STEP_KEY);
      if (step !== 'promo-bonus') {
        localStorage.setItem(REGISTRATION_STEP_KEY, 'promo-bonus');
        gtag(
          {
            section: 'RegistrationStepPromoBonus',
            detail: 'RegistrationStepPromoBonus',
            event: 'RegistrationStepPromoBonus',
          },
          true
        );
      }
    }
  }, [registrationUrl, asPath]);

  const submitWholeForm: SubmitHandler<BonusCodeValidation> = () => {
    dispatch(setPromoCode({ promoCode: getValues('promocode') }));
    next(true);
  };

  return (
    <div className={styles.formContainer}>
      {isBlockVisible ? (
        <>
          <div className={styles.title}>{labels.titlePromoBonus}</div>
          <RichText containerClassName={styles.subTitle}>{labels.descriptionPromoBonus}</RichText>
          <BonusCarousel
            listOfItem={bonusListRegistration}
            handlePromoSelected={handlePromoSelected}
            promoHasMatched={promoHasMatched}
            bonusSelected={bonusSelected}
          />
          <div className={styles.dividerContainer}>
            <hr className={styles.divider} /> <span>{labelOppure}</span>
            <hr className={styles.divider} />
          </div>
          <div className={styles.subTitle}>{labels.descriptionPromoCode}</div>
        </>
      ) : (
        <>
          <div className={styles.title}>{labels.titlePromoBonusAffiliate}</div>
          <RichText containerClassName={styles.subTitle}>{labels.descriptionPromoBonusAffiliate}</RichText>
        </>
      )}
      <form onSubmit={handleSubmit(submitWholeForm)} className={styles.form}>
        <TextInput
          name="promocode"
          control={control}
          label={labels.promoCode}
          placeholder={labels.placeholderPromoCode}
          disabled={!isBlockVisible && !!state.promoCode}
        ></TextInput>
        <ButtonRegistrationWrapper handleGoBack={back!} isNextDisabled={!formState.isValid} />
      </form>
    </div>
  );
};
